// components/document-settings/LegalBlocksTab.jsx
// Gestione blocchi legali documenti

import React from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { BookOpen, Plus, Edit, Trash2, Save, Check, X, ChevronRight } from 'lucide-react';

const documentTypeLabels = {
  'estimate_customer': 'Preventivo Cliente',
  'estimate_detailed': 'Preventivo Dettagliato',
  'contract_customer': 'Contratto Cliente',
  'sal_report': 'Report SAL',
  'invoice_notice': 'Avviso di Fattura'
};

export const LegalBlocksTab = ({
  legalBlocks,
  loading,
  showLegalBlockModal,
  setShowLegalBlockModal,
  editingLegalBlock,
  setEditingLegalBlock,
  legalBlockForm,
  setLegalBlockForm,
  onCreateLegalBlock,
  onUpdateLegalBlock,
  onDeleteLegalBlock,
  onToggleLegalBlock
}) => {
  const resetForm = () => {
    setLegalBlockForm({
      block_title: '',
      block_content: '',
      document_type: 'estimate_customer',
      display_order: 0
    });
    setEditingLegalBlock(null);
  };

  const handleEdit = (block) => {
    setEditingLegalBlock(block);
    setLegalBlockForm({
      block_title: block.block_title,
      block_content: block.block_content,
      document_type: block.document_type,
      display_order: block.display_order
    });
    setShowLegalBlockModal(true);
  };

  // Group blocks by document type
  const blocksByType = legalBlocks.reduce((acc, block) => {
    const type = block.document_type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(block);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Blocchi Legali</h3>
          <p className="text-sm text-gray-500">Clausole e testi legali per i documenti</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowLegalBlockModal(true);
          }} 
          className="bg-[#FF7819] hover:bg-[#E56A10]"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Nuovo Blocco
        </Button>
      </div>

      {Object.entries(blocksByType).map(([docType, blocks]) => (
        <div key={docType} className="space-y-2">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <ChevronRight className="w-4 h-4" />
            {documentTypeLabels[docType] || docType}
            <Badge variant="outline">{blocks.length}</Badge>
          </h4>
          <div className="grid gap-3 ml-6">
            {blocks.sort((a, b) => a.display_order - b.display_order).map(block => (
              <Card key={block.id} className={!block.is_active ? 'opacity-60' : ''}>
                <CardContent className="py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium">{block.block_title}</h5>
                        <Badge variant="outline" className="text-xs">
                          Ordine: {block.display_order}
                        </Badge>
                        {block.is_active ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">Attivo</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 text-xs">Disattivato</Badge>
                        )}
                      </div>
                      <div 
                        className="text-sm text-gray-600 mt-2 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: DOMPurify.sanitize(
                            block.block_content.substring(0, 200) + 
                            (block.block_content.length > 200 ? '...' : '')
                          ) 
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Switch
                        checked={block.is_active}
                        onCheckedChange={() => onToggleLegalBlock(block.id, !block.is_active)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(block)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600"
                        onClick={() => onDeleteLegalBlock(block.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {legalBlocks.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nessun blocco legale configurato</p>
            <p className="text-sm">Aggiungi clausole e testi per i tuoi documenti</p>
          </CardContent>
        </Card>
      )}

      {/* Legal Block Modal */}
      <Dialog open={showLegalBlockModal} onOpenChange={setShowLegalBlockModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingLegalBlock ? 'Modifica Blocco Legale' : 'Nuovo Blocco Legale'}</DialogTitle>
            <DialogDescription>
              Configura il contenuto del blocco legale
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titolo Blocco</Label>
                <Input
                  value={legalBlockForm.block_title}
                  onChange={e => setLegalBlockForm({...legalBlockForm, block_title: e.target.value})}
                  placeholder="es. Condizioni di Pagamento"
                />
              </div>
              <div>
                <Label>Ordine Visualizzazione</Label>
                <Input
                  type="number"
                  value={legalBlockForm.display_order}
                  onChange={e => setLegalBlockForm({...legalBlockForm, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <Label>Tipo Documento</Label>
              <Select 
                value={legalBlockForm.document_type}
                onValueChange={v => setLegalBlockForm({...legalBlockForm, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(documentTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contenuto (HTML supportato)</Label>
              <Textarea
                value={legalBlockForm.block_content}
                onChange={e => setLegalBlockForm({...legalBlockForm, block_content: e.target.value})}
                placeholder="Inserisci il testo del blocco legale..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLegalBlockModal(false)}>
              Annulla
            </Button>
            <Button 
              onClick={editingLegalBlock ? onUpdateLegalBlock : onCreateLegalBlock}
              disabled={loading}
              className="bg-[#FF7819] hover:bg-[#E56A10]"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {editingLegalBlock ? 'Aggiorna' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegalBlocksTab;
